import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import GuideSpotlight from "./GuideSpotlight";
import { waitForElement } from "./guideUtils";

// Runs in the /storefront-scanner tab that opens when the user clicks the
// storefront "stalk" magnifier during the step-4 walkthrough. A localStorage
// handoff flag (set by StorefrontGuideTrigger) tells us to highlight SCAN.
//
//   1. Spotlight the SCAN button (#sfScanBtn).
//   2. After they scan (or hit "Got it") -> a note to scroll for the results.
const SCAN_FLAG = "nepeto_scan_guide";
const MAX_AGE_MS = 120000; // ignore a stale flag older than 2 min

function StorefrontScanGuideTrigger({ isMobile }) {
  const location = useLocation();
  const [step, setStep] = useState(0); // 0 off, 1 scan button, 2 scroll note
  const startedRef = useRef(false);

  useEffect(() => {
    if (location.pathname !== "/storefront-scanner") {
      startedRef.current = false;
      setStep(0); // never leave the blocking overlay up on another page
      return;
    }
    if (startedRef.current) return;

    const raw = localStorage.getItem(SCAN_FLAG);
    if (!raw) return;
    localStorage.removeItem(SCAN_FLAG); // consume it (even if stale/corrupt)
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > MAX_AGE_MS) return; // stale/corrupt

    startedRef.current = true;
    let btn = null;
    let onScan = null;
    const cancel = waitForElement("#sfScanBtn", {
      tries: 40,
      interval: 300,
      requireVisible: false,
      onFound: (el) => {
        btn = el;
        setStep(1);
        onScan = () => setStep(2);
        btn.addEventListener("click", onScan, { once: true });
      },
      onTimeout: () => {
        startedRef.current = false;
      },
    });

    return () => {
      cancel();
      if (btn && onScan) btn.removeEventListener("click", onScan);
    };
  }, [location]);

  if (step === 1) {
    return (
      <GuideSpotlight
        targetSelector="#sfScanBtn"
        title="Scan the storefront"
        content="Click SCAN to scan this seller's entire storefront and see everything they sell."
        nextLabel="Got it"
        onNext={() => setStep(2)}
        isMobile={isMobile}
      />
    );
  }
  if (step === 2) {
    return (
      <GuideSpotlight
        targetSelector={null}
        title="Last step!"
        content="Once the results load, scroll down to see all the products this seller sells."
        nextLabel="Done"
        onNext={() => setStep(0)}
        isMobile={isMobile}
      />
    );
  }
  return null;
}

export default StorefrontScanGuideTrigger;
